<template>
  <div class="sal-portal-container">
    <div class="version-ctrl-panel-container">
      <div id="sal-version" class="version">{{ version }}</div>
      <!--dat-gui id="control-panel" close-text="Close controls" open-text="Open controls" close-position="bottom"
        :closed="true">
        <dat-select v-model="pdfPrintConfig.pdfOrientation" :items="pdfOrientations" label="orientation" />
        <dat-select v-model="pdfPrintConfig.pdfFormat" :items="pdfFormats" label="PDF Format" />
      </dat-gui-->
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="loading-message">
      Loading {{ componentDisplayName }}...
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="error-message">
      <h3>Component Load Error</h3>
      <p>{{ error }}</p>
      <p><strong>Requested component:</strong> {{ ($route && $route.params && $route.params.componentName) || 'Unknown' }}</p>
    </div>
    
    <!-- Component Display -->
    <component 
      v-else-if="componentLoaded && dynamicComponent" 
      :is="dynamicComponent" 
      :pdf-print-config="pdfPrintConfig"
      :key="(route && route.params && route.params.componentName) || 'default'"
    >
    </component>
    
    <!-- No Component State -->
    <div v-else class="no-component-message">
      No component specified or component not found.
    </div>
  </div>
</template>

<script>
import { useRoute } from 'vue-router';
import { ref, onMounted, watch, computed } from 'vue';

import "@pdf/core";
import Utils from "../utils"
import EnvLauncher from '../mixins/EnvLauncher'

export default {
  name: 'DynamicComponentRenderer',
  mixins: [EnvLauncher],
  setup() {
    const route = useRoute();
    const shareClassId = route?.query?.shareClassId;
    const securityType = route?.query?.securityType;
    const fundServCode = route?.query?.fundServCode;
    const pdfOrientations = Utils.pdfOrientations;
    const pdfFormats = Utils.pdfFormats;
    const version = ref("");
    const componentLoaded = ref(false);
    const dynamicComponent = ref(null);
    const loading = ref(false);
    const error = ref(null);
    
    const pdfPrintConfig = ref({
      pdfHeaderText: undefined,
      pdfFooterText: undefined,
      pdfOrientation: "portrait",
      pdfFormat: "Letter",
    });

    // Dynamic component configuration generator
    const generateComponentConfig = (componentName) => {
      if (!componentName) return null;
      
      // Generate display name from component name
      const generateDisplayName = (name) => {
        return name
          .replace('pdf-', '')
          .replace(/-/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase())
          .replace(/^Esg/, 'ESG')
          .replace(/Eqsv/, 'EQSV');
      };
      
      // Determine config type based on component name
      const getConfigType = (name) => {
        if (name.startsWith('pdf-eqsv-')) return 'eqsv';
        if (name.startsWith('pdf-esg-')) return 'esg';
        if (name.startsWith('pdf-equity-')) return 'equity';
        if (name.startsWith('pdf-fund-')) return 'fund';
        // Default fallback
        return 'esg';
      };
      
      // Generate NPM package name from component name
      const generateNpmPackage = (name) => {
        // Remove 'pdf-' prefix and replace with '@pdf/'
        return `@pdf/${name.replace('pdf-', '')}`;
      };
      
      return {
        name: generateDisplayName(componentName),
        value: componentName,
        cdnPath: `${componentName}.js`,
        npmPackage: generateNpmPackage(componentName),
        windowGlobal: componentName,
        configType: getConfigType(componentName)
      };
    };

    // Get current component config based on route parameter
    const currentComponentConfig = computed(() => {
      const componentName = route?.params?.componentName;
      return generateComponentConfig(componentName);
    });

    // Display name for the current component
    const componentDisplayName = computed(() => {
      return currentComponentConfig.value ? currentComponentConfig.value.name : route?.params?.componentName || 'Unknown Component';
    });

    // Dynamic application config setup
    const setupApplicationConfig = (componentConfig) => {
      const applicationConfig = {
        integration: {
          id: "PDF-DEMO-APP",
        },
        investment: {
          id: shareClassId,
          type: securityType,
          marketUserGroup: "",
          fundServCode,
        },
        labels: {},
        components: {
          SalBasicPage: {
            type: `@pdf/${componentConfig.configType}`,
            settings: {},
            labels: {},
          },
        },
      };
      window.sal.configuration.setAppConfig(applicationConfig);
    };

    // Dynamic component loading method
    const loadComponent = async (componentConfig) => {
      if (!componentConfig) {
        error.value = `Invalid component name '${route?.params?.componentName || 'undefined'}'.`;
        loading.value = false;
        return;
      }
      
      try {
        loading.value = true;
        error.value = null;
        componentLoaded.value = false;
        
        // Setup application config for the selected component
        setupApplicationConfig(componentConfig);
        
        const host = `${window.location.protocol}//${window.location.host}`;
        let cdnUrl = `${host}/mf3/latest/${componentConfig.cdnPath}`;

        if (host.includes("pdfportal-stg")) {
          const pathName = window.location.pathname;
          const match = pathName.match(/(\d+\.)(\d+\.)(\d+)/);
          cdnUrl = match
            ? `${host}/mf3/v${match[0]}/${componentConfig.cdnPath}`
            : `${host}/mf3/latestRC/${componentConfig.cdnPath}`;
        }
        if (host.includes("pdfportal-uat")) {
          cdnUrl = `${host}/mf3/latestRC/${componentConfig.cdnPath}`;
        }
        if (host.includes("pdfportal-dev") || host.includes("pdfportal-qa")) {
          cdnUrl = `${host}/mf3/latestCI/${componentConfig.cdnPath}`;
        }
        if (host.includes("localhost")) {
          cdnUrl = `https://pdfportal-qa.psp98d75.easn.morningstar.com/mf3/latestCI/${componentConfig.cdnPath}`;
        }

        try {
          // Load CDN first - make Vue available globally for UMD build
          const vue = await import('vue');
          window.Vue = vue;
          await Utils.salImport(cdnUrl);

          if (window[componentConfig.windowGlobal]) {
            const component = window[componentConfig.windowGlobal].default || window[componentConfig.windowGlobal];
            if (component) {
              dynamicComponent.value = component;
              componentLoaded.value = true;
              return;
            }
          }

          throw new Error("CDN component not available");

        } catch (cdnError) {
          // Fallback to node_modules
          try {
            const moduleImport = await import(componentConfig.npmPackage);
            const component = moduleImport.default || moduleImport;

            if (component) {
              dynamicComponent.value = component;
              componentLoaded.value = true;
            } else {
              throw new Error("Failed to load component from both CDN and node_modules");
            }
          } catch (fallbackError) {
            console.error("Component loading failed:", fallbackError);
            error.value = `Failed to load component: ${fallbackError.message}`;
          }
        }
      } catch (unexpectedError) {
        console.error('Unexpected error during component loading:', unexpectedError);
        error.value = `Unexpected error: ${unexpectedError.message}`;
        componentLoaded.value = false;
      } finally {
        loading.value = false;
      }
    };

    // Watch for route changes
    watch(() => route?.params?.componentName, async (newComponentName, oldComponentName) => {
      // Only proceed if we have a valid route
      if (!route || !route.params) {
        error.value = 'Route not available';
        return;
      }
      
      // Clear previous component first to prevent DOM conflicts
      if (oldComponentName !== newComponentName) {
        componentLoaded.value = false;
        dynamicComponent.value = null;
        error.value = null;
      }
      
      if (newComponentName) {
        const componentConfig = generateComponentConfig(newComponentName);
        await loadComponent(componentConfig);
      }
    }, { immediate: true });

    return {
      shareClassId,
      pdfOrientations,
      pdfPrintConfig,
      pdfFormats,
      componentLoaded,
      dynamicComponent,
      loading,
      error,
      componentDisplayName,
      version
    }
  }
}
</script>

<style scoped>
.component-info {
  padding: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  margin: 5px 0;
  font-size: 12px;
  color: #333;
}

.loading-message, .error-message, .no-component-message {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
  margin: 20px;
  border-radius: 8px;
}

.loading-message {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.error-message {
  background-color: #ffebee;
  color: #d32f2f;
  border: 1px solid #f8bbd9;
}

.no-component-message {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
}

.error-message h3 {
  margin-top: 0;
}
</style>